import React, { Fragment, useEffect, useState, useRef } from "react";
import { FC } from "react";
import DatePicker from "react-datepicker";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";

import { changeDate } from "utils/changeDate";
import { useData } from "data/data-provider";

import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import toast from "react-hot-toast";

import moment from "moment";
import ClearDataButton from "./ClearDataButton";
import DatePickerCustomDay from "../DatePickerCustomDay";
import DatePickerCustomHeaderOneMonth from "../DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "../DatePickerCustomHeaderTwoMonth";
import { formatDateToHumanFirendly } from "utils/date-format";

interface UnavailabilityData {
  full_unavailable: string[];
  partial_unavailable: { [key: string]: [string, string][] };
}

export interface RentalCarDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  selectedCar?: any;
  fleetId?: number;
  isMiddle?: boolean;
  searchBar?: boolean;
  minDays?: number | null | undefined;
  maxDays?: number | null | undefined;
  bookingDetails?: any;
  isStartDateLocked?: boolean;
  setIsStartDateLocked?: (value: boolean) => void;
}

const RentalCarDatesRangeInput: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  fleetId,
  isMiddle,
  searchBar,
  minDays,
  maxDays,
  bookingDetails,
  isStartDateLocked,
  setIsStartDateLocked,
}) => {
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    pickupLocation,
  } = useData();

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  // Helper for formatting date to "YYYY-MM-DD"
  const formatDate = (date: Date) => moment(date).format("YYYY-MM-DD");

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Initialize temp dates from context
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  const monthsToShow = isLargeScreen ? 2 : 1;

  const parsePartialUnavailability = (partialUnavailable: {
    [key: string]: [string, string][];
  }) => {
    const result: { [key: string]: { from: string; to: string }[] } = {};

    for (const [dateKey, timeRanges] of Object.entries(partialUnavailable)) {
      result[dateKey] = timeRanges.map(([fromTime, toTime]) => ({
        from: `${fromTime}:00`,
        to: `${toTime}:00`,
      }));
    }

    return result;
  };

  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 3);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);
      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
          booking_to_exclude: bookingDetails?.is_long_term_rental
            ? (bookingDetails?.main_booking ?? bookingDetails?.id)
            : bookingDetails?.id,
        },
        selectedCar.id,
        pickupLocation?.id,
        true, // Exclude advance notice bookings to show more accurate availability for last-minute bookings
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr) => createDateWithoutTimezone(dateStr),
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable,
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const createDateWithoutTimezone = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const isDateSelectable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isFullyUnavailable = fullyUnavailableDates.some(
      (unavailableDate) =>
        unavailableDate.toDateString() === date.toDateString(),
    );
    if (isFullyUnavailable) return false;

    let pickUpDate = today;
    if (bookingDetails?.pick_up_time) {
      const bookingPickUp = new Date(bookingDetails.pick_up_time);
      bookingPickUp.setHours(0, 0, 0, 0);
      if (bookingPickUp < today) {
        pickUpDate = bookingPickUp;
      }
    }

    if (date < pickUpDate) return false;

    return true;
  };

  const isRangeAvailable = (start: moment.Moment, end: moment.Moment) => {
    const unavailableSet = new Set(
      fullyUnavailableDates.map((date) => moment(date).format("YYYY-MM-DD")),
    );

    let currentDate = start.clone();
    while (currentDate.isSameOrBefore(end, "day")) {
      if (unavailableSet.has(currentDate.format("YYYY-MM-DD"))) {
        return false;
      }
      currentDate.add(1, "day");
    }
    return true;
  };

  const isPartialOverlapValid = (
    start: moment.Moment,
    end: moment.Moment,
    partialUnavailability: Record<string, any>,
  ) => {
    const partialDates = Object.keys(partialUnavailability);

    for (let date of partialDates) {
      const dayMoment = moment(date, "YYYY-MM-DD");
      if (dayMoment.isAfter(start, "day") && dayMoment.isBefore(end, "day")) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (bookingDetails?.pick_up_time) {
      const pickUpDateTime = new Date(bookingDetails.pick_up_time);
      const currentDateTime = new Date();
      const pickUpDateOnly = new Date(pickUpDateTime);
      pickUpDateOnly.setHours(0, 0, 0, 0);
      const currentDateOnly = new Date(currentDateTime);
      currentDateOnly.setHours(0, 0, 0, 0);
      const shouldLock = 
        pickUpDateOnly < currentDateOnly || 
        (pickUpDateOnly.getTime() === currentDateOnly.getTime() && pickUpDateTime < currentDateTime);

      if (shouldLock) {
        setIsStartDateLocked?.(true);
        if (
          setStartDate &&
          (!startDate || startDate.getTime() !== pickUpDateTime.getTime())
        ) {
          setStartDate(pickUpDateTime);
        }
      }
    }
  }, [bookingDetails?.pick_up_time, setStartDate, setIsStartDateLocked]);

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    let startDateValue = dates[0];
    if (isStartDateLocked && bookingDetails?.pick_up_time) {
      startDateValue = new Date(
        formatDateToHumanFirendly(bookingDetails.pick_up_time),
      );
    }
    const startMoment = moment(startDateValue);
    const endMoment = moment(dates[1]);
    const isCheck = isRangeAvailable(startMoment, endMoment);
    if (!isCheck) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error("Selected range is fully unavailable.");
      return;
    }

    const isPartialValid = isPartialOverlapValid(
      startMoment,
      endMoment,
      partialUnavailability,
    );
    if (!isPartialValid) {
      setTempStartDate(startDateValue);
      setTempEndDate(null);
      toast.error(
        "Selected range contains a partially unavailable date in the middle.",
      );
      return;
    }

    setTempStartDate(startDateValue);
    setTempEndDate(dates[1]);
  };

  const handleSaveDates = () => {
    if (tempStartDate && tempEndDate) {
      changeDate(
        [tempStartDate, tempEndDate],
        setStartDate,
        setEndDate,
        startTime,
        endTime,
      );
      
      const today = new Date();
      const visibleEndDate = new Date(today);
      visibleEndDate.setMonth(today.getMonth() + monthsToShow);
      fetchUnavailableDates(today, visibleEndDate);
    }
  };

  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-text-color">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span
            className={`block xl:text-lg font-semibold ${"text-text-color"}`}
          >
                  {startDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
            }) || "Add dates"}
            {endDate
              ? " - " +
                endDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                })
              : ""}
                </span>
          <span
            className={`block mt-1 text-sm ${"text-text-color"} leading-none font-light`}
          >
            {"Pickup & Drop off Date"}
          </span>
        </div>
      </>
    );
  };
  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isFullyUnavailable = fullyUnavailableDates.some(
      (d) => d.toDateString() === date.toDateString(),
    );
    const isPartial = !!partialUnavailability[dateStr];
    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
      />
    );
  };

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput z-20 relative flex ${className}`}
      >
        {({ open }) => {
          return (
            <>
              <div
                className={`flex-1 z-10 flex items-center focus:outline-none ${
                  open ? "" : ""
                }`}
              >
                <PopoverButton
                  className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none `}
                  onClickCapture={() => document.querySelector("html")?.click()}
                >
                  {renderInput()}

                  {startDate && open && (
                      <ClearDataButton
                        onClick={() => {
                            setStartDate(null);
                            setEndDate(null);
                            setTempStartDate(null);
                            setTempEndDate(null);
                            setPartiallyAvailableStartDate(null);
                            setPartiallyAvailableEndDate(null);
                        }}
                      />
                    )}
                </PopoverButton>
              </div>

              {open && (
                <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1 "></div>
              )}

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className={`absolute left-1/2 z-40 mt-3 top-full w-screen max-w-2xl -translate-x-1/2 ${
                    isMiddle ? "lg:-translate-x-1/3" : "-translate-x-1/2"
                  } xl:-translate-x-1/2 transform px-4 sm:px-0 lg:max-w-3xl`}
                >
                  <div className="overflow-hidden rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bg-secondary-50 p-8">
                    {isLoading ? (
                      <div className="pointer-events-none opacity-60 select-none">
                        <div className="relative">
                          {/* Calendar is visible but faded and unclickable */}
                          <div className="flex items-center space-x-4 mb-4">
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                                style={{ background: "green" }}
                              />
                              <span className="text-xs text-text-color">
                                Available
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                                style={{
                                  background:
                                    "linear-gradient(to right, red 50%, green 50%)",
                                }}
                              />
                              <span className="text-xs text-text-color">
                                Partially Available
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                                style={{ background: "red" }}
                              />
                              <span className="text-xs text-text-color">
                                Unavailable
                              </span>
                            </div>
                          </div>
                          <DatePicker
                            selected={
                              isStartDateLocked && bookingDetails?.pick_up_time
                                ? new Date(
                                    formatDateToHumanFirendly(
                                      bookingDetails.pick_up_time,
                                    ),
                                  )
                                : tempStartDate
                            }
                            onChange={onChangeDate}
                            startDate={
                              isStartDateLocked && bookingDetails?.pick_up_time
                                ? new Date(
                                    formatDateToHumanFirendly(
                                      bookingDetails.pick_up_time,
                                    ),
                                  )
                                : tempStartDate
                            }
                            endDate={tempEndDate}
                            selectsRange
                            monthsShown={monthsToShow}
                            showPopperArrow={false}
                            inline
                            minDate={bookingDetails?.startDate}
                            openToDate={
                              calendarViewDate ||
                              (isStartDateLocked && bookingDetails?.pick_up_time
                                ? new Date(
                                    formatDateToHumanFirendly(
                                      bookingDetails.pick_up_time,
                                    ),
                                  )
                                : tempStartDate) ||
                              new Date()
                            }
                            filterDate={isDateSelectable}
                            renderCustomHeader={(p) => (
                              <>
                                <div className="block sm:hidden text-text-color">
                                  <DatePickerCustomHeaderOneMonth {...p} />
                                </div>
                                <div className="hidden sm:block text-text-color">
                                  <DatePickerCustomHeaderTwoMonth {...p} />
                                </div>
                              </>
                            )}
                            renderDayContents={renderDayContents}
                            onMonthChange={handleMonthChange}
                          />
                        </div>

                        <div className="absolute left-0 bottom-0 w-full px-8">
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                            <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                          </div>
                        </div>

                        <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{ background: "green" }}
                            />
                            <span className="text-xs text-text-color">
                              Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{
                                background:
                                  "linear-gradient(to right, red 50%, green 50%)",
                              }}
                            />
                            <span className="text-xs text-text-color">
                              Partially Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                              style={{ background: "red" }}
                            />
                            <span className="text-xs text-text-color">
                              Unavailable
                            </span>
                          </div>
                        </div>
                        {isStartDateLocked && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                            The pickup date is locked because it's in the
                            past. You can only modify the drop-off date.
                          </div>
                        )}
                        <DatePicker
                          selected={tempStartDate}
                          onChange={onChangeDate}
                          startDate={tempStartDate}
                          endDate={tempEndDate}
                          selectsRange
                          monthsShown={monthsToShow}
                          showPopperArrow={false}
                          inline
                          minDate={bookingDetails?.startDate}
                          openToDate={
                            calendarViewDate || tempStartDate || new Date()
                          }
                          filterDate={isDateSelectable}
                          renderCustomHeader={(p) => (
                            <>
                              <div className="block sm:hidden text-text-color">
                                <DatePickerCustomHeaderOneMonth {...p} />
                              </div>
                              <div className="hidden sm:block text-text-color">
                                <DatePickerCustomHeaderTwoMonth {...p} />
                              </div>
                            </>
                          )}
                          renderDayContents={renderDayContents}
                          onMonthChange={handleMonthChange}
                        />
                      </div>
                    )}
                    <Popover.Button
                      className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-[#121212] font-semibold rounded-lg py-3 transition-all flex items-center justify-center space-x-2"
                      onClick={handleSaveDates}
                    >
                      <span className="text-white">Save Dates</span>
                    </Popover.Button>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          );
        }}
      </Popover>
    </>
  );
};
export default RentalCarDatesRangeInput;